import { Avatar, Button, CircularProgress, Stack, Typography } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyGetGrowthToolLikesQuery } from '../../api/growthTools'
import type { IGrowthToolLikeUserResource } from '../../api/types/growthTools'
import { CURSOR_LIST_PAGE_SIZE } from '../../consts/api'
import { routesProfile } from '../../consts'
import modalEmitter from '../ModalComponent/modalEmitter'
import {
  canNavigateToUserProfile,
  getUserInitialsFromDisplayName,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from '../../utils/deletedUserDisplay'

export const GrowthToolLikesModalBody = (props: { growthToolId: number }) => {
  const { growthToolId } = props
  const navigate = useNavigate()

  const [cursor, setCursor] = React.useState<string | null>(null)
  const [items, setItems] = React.useState<IGrowthToolLikeUserResource[]>([])
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetGrowthToolLikesQuery()

  React.useEffect(() => {
    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ growthToolId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [growthToolId, trigger])

  React.useEffect(() => {
    if (cursor == null) return
    trigger({ growthToolId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, growthToolId, trigger])

  React.useEffect(() => {
    if (!data) return
    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.data ?? [])] : data.data ?? []))
  }, [data, cursor])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  const handleOpenProfile = (slug: string) => {
    const pathKey = slug.trim()
    if (!pathKey) return
    modalEmitter.emitCloseModal()
    navigate(`/${routesProfile.USERS.path}/${pathKey}`)
  }

  return (
    <Stack gap={2}>
      {isLoading && (
        <Stack direction="row" gap={2} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Загружаем лайки...</Typography>
        </Stack>
      )}

      {isError && <Typography color="error">Не удалось загрузить список лайков.</Typography>}

      {!isLoading && !isError && items.length === 0 && <Typography>Пока никто не поставил лайк.</Typography>}

      {items.map((u, idx) => {
        const title = resolveUserDisplayName(u)
        const slug = (u.slug ?? '').trim()
        const canOpenProfile = canNavigateToUserProfile(u)
        return (
          <Stack
            key={`${u.id ?? 'deleted'}-${idx}`}
            direction="row"
            gap={2}
            alignItems="center"
            sx={{ cursor: canOpenProfile ? 'pointer' : 'default' }}
            onClick={canOpenProfile ? () => handleOpenProfile(slug) : undefined}
          >
            <Avatar src={resolveUserAvatarUrl(u)} alt={title}>
              {getUserInitialsFromDisplayName(title)}
            </Avatar>
            <Typography>{title}</Typography>
          </Stack>
        )
      })}

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="follow" disabled={!canLoadMore} onClick={() => setCursor(nextCursor)}>
          {isFetching ? 'Загрузка...' : 'Показать ещё'}
        </Button>
      </Stack>
    </Stack>
  )
}
